import { AnyAction, createAsyncThunk } from '@reduxjs/toolkit';
import { batch } from 'react-redux';

import { CancelOrderPayload, ClosedOrdersPayload } from 'api/market/dto';
import MarketService from 'api/market/MarketService';
import { downloadOrders } from 'utils/downloadCsv';
import { getNotification } from 'helpers';
import { throttle } from 'utils';
import { 
  NotificationType,
  Notifications,
  OrderStatus,
  OrdersViewTabs,
  RequestStatus,
  OrderT,
} from 'types';

import { addNotification } from 'store/entities/notification/slice';
import {
  setOpenOrdersStatus,
  setOpenOrders,
  setUpdatedOrder,
  setClosedOrders,
  setClosedOrdersStatus,
  setCreateOrderStatus,
  setCancelOrderStatus,
  setCurrentHistoryPage,
} from './slice';

export const getOpenOrders = createAsyncThunk(
  'market/getOpenOrders',
  throttle(async (address: string, { dispatch, extra }: any) => {
    try {
      dispatch(setOpenOrdersStatus(RequestStatus.Pending));
      const marketService = extra.marketService as MarketService;
      const { open_orders } = await marketService.getOpenOrders(address);

      batch(() => {
        dispatch(setOpenOrders(open_orders));
        dispatch(setOpenOrdersStatus(RequestStatus.Success));
      });
    } catch (error) {
      batch(() => {
        dispatch(
          addNotification({ type: NotificationType.Canceled, msg: getNotification(Notifications.TryAgain, _t) })
        );
        dispatch(setOpenOrdersStatus(RequestStatus.Failed));
      });
    }
  }, 500)
) as (address: string) => any;

export const getClosedOrders = createAsyncThunk(
  'orders/getClosedOrders',
  throttle(async (params: ClosedOrdersPayload, { dispatch, extra }: any) => {
    try {
      const { address, offset, limit } = params;
      dispatch(setClosedOrdersStatus(RequestStatus.Pending));
      const marketService = extra.marketService as MarketService;
      const { closed_orders, closed_orders_count } = await marketService.getClosedOrders(address, offset, limit);

      batch(() => {
        if (!offset) {
          dispatch(setCurrentHistoryPage(1));
        }
        dispatch(setClosedOrders({ closed_orders, closed_orders_count }));
        dispatch(setClosedOrdersStatus(RequestStatus.Success));
      });
    } catch (error) {
      batch(() => {
        dispatch(
          addNotification({ type: NotificationType.Canceled, msg: getNotification(Notifications.TryAgain, _t) })
        );
        dispatch(setClosedOrdersStatus(RequestStatus.Failed));
      });
    }
  }, 200)
) as (params: ClosedOrdersPayload) => any;

export const getAllOrders = createAsyncThunk(
  'orders/getAllOrders',
  throttle(async (address: `0x${string}`, { dispatch }: any) => {
    try {
      batch(() => {
        dispatch(getOpenOrders(address));
        dispatch(getClosedOrders({ address }));
      });
    } catch (error) {
      console.log(error);
    }
  }, 500)
) as (address: `0x${string}`) => any;

export const downloadOrdersCsv = createAsyncThunk(
  'orders/downloadOrdersCsv',
  throttle(async ({ address, type }: { address: string; type: string }, { dispatch, extra }: any) => {
    try {
      const marketService = extra.marketService as MarketService;

      let url = '';
      if (type === OrdersViewTabs.OrderHistory) {
        const { closed_orders_csv } = await marketService.downloadClosedOrdersCsv(address);
        url = closed_orders_csv;
      } else {
        const { open_orders_csv } = await marketService.downloadOpenOrdersCsv(address);
        url = open_orders_csv;
      }

      await downloadOrders(url);
    } catch (error) {
      dispatch(addNotification({ type: NotificationType.Canceled, msg: getNotification(Notifications.TryAgain, _t) }));
    }
  }, 500)
) as ({ address, type }: { address: string; type: string }) => any;

export const createOrder = createAsyncThunk(
  'orders/createOrder',
  throttle(async (address: string, { dispatch, extra }: any) => {
    dispatch(setCreateOrderStatus(RequestStatus.Pending));
    try {
      const marketService = extra.marketService as MarketService;
      await marketService.createOrder(address);

      batch(() => {
        dispatch(setCreateOrderStatus(RequestStatus.Success));
        dispatch(
          addNotification({
            type: NotificationType.Submitted,
            msg: getNotification(Notifications.CreatedOrderSuccess, _t),
          })
        );
      });
    } catch (error) {
      batch(() => {
        dispatch(setCreateOrderStatus(RequestStatus.Failed));
        dispatch(
          addNotification({
            type: NotificationType.Canceled,
            msg: getNotification(Notifications.CreatedOrderError, _t),
          })
        );
      });
    }
  }, 1500)
) as unknown as (address: string) => AnyAction;

export const cancelOrder = createAsyncThunk(
  'orders/cancelOrder',
  throttle(async (data: CancelOrderPayload, { dispatch, extra }: any) => {
    dispatch(setCancelOrderStatus(RequestStatus.Pending));
    try {
      const marketService = extra.marketService as MarketService;
      await marketService.cancelOrder(data.id, data.address);

      batch(() => {
        dispatch(setCancelOrderStatus(RequestStatus.Success));
        if (data.isReclaim) {
          dispatch(
            addNotification({
              type: NotificationType.Submitted,
              msg: getNotification(Notifications.ReclaimOrderSuccess, _t),
            })
          );
          return;
        }
        dispatch(
          addNotification({
            type: NotificationType.Submitted,
            msg: getNotification(Notifications.CancelOrderSuccess, _t),
          })
        );
      });
    } catch (error) {
      batch(() => {
        dispatch(setCancelOrderStatus(RequestStatus.Failed));
        if (data.isReclaim) {
          dispatch(
            addNotification({
              type: NotificationType.Canceled,
              msg: getNotification(Notifications.ReclaimOrderError, _t),
            })
          );
          return;
        }
        dispatch(
          addNotification({ type: NotificationType.Canceled, msg: getNotification(Notifications.CancelOrderError, _t) })
        );
      });
    }
  }, 500)
) as (data: CancelOrderPayload) => any;

export const subscribeOpenOrders = createAsyncThunk(
  'orders/subscribeOpenOrders',
  throttle(async (address: `0x${string}` | undefined, { dispatch, extra }: any) => {
    try {
      const marketService = extra.marketService as MarketService;

      if (address) {
        dispatch(getOpenOrders(address));

        marketService.subscribeOpenOrders(address, {
          next: ({ data: { open_orders: updatedOrder } }: { data: { open_orders: OrderT } }) => {
            const { status } = updatedOrder;
            if (
              status === OrderStatus.Canceled ||
              status === OrderStatus.CompletelyFilled ||
              status === OrderStatus.Error
            ) {
              dispatch(getClosedOrders({ address }));
            }

            dispatch(setUpdatedOrder(updatedOrder));
          },
          error: () => {},
          complete: () => {},
        });
      } else {
        marketService.unsubscribeOpenOrders();
      }
    } catch (error) {
      batch(() => {
        dispatch(
          addNotification({ type: NotificationType.Canceled, msg: getNotification(Notifications.TryAgain, _t) })
        );
        console.log(error);
      });
    }
  }, 1000)
) as (address: `0x${string}` | undefined) => any;